import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Section from '../../../components/Main/Section/Section';
import ActionBar from '../../../components/ActionBar/ActionBar';
import Filter from '../../../components/Filter/Filter';
import Chip from '../../../components/Chip/Chip';
import Pagination from '../../../components/Pagination/Pagination';
import GoogleMyBusinessLogin from '../../../components/GoogleMyBusiness/GoogleMyBusinessLogin';
import LocationHistoryCard from '../../../components/LocationCard/LocationHistoryCard';
import LeftContainer from '../../../components/Containers/LeftContainer';
import RightContainer from '../../../components/Containers/RightContainer';
import SortContainer from '../../../components/Containers/SortContainer';
import ContentContainer from '../../../components/Containers/ContentContainer';
import SliderStarsAsLabels from '../../../components/Sliders/StarsAsLabels/SliderStarsAsLabels';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import { GMBAuthContext } from '../../../providers/GMBAuthProvider';
import GMBService from '../../../services/GMBService';
import { IGmbLocation } from '../../../shared/interfaces/IGmbLocation';
import { IGMBLocationsResponse } from '../../../shared/interfaces/IGMBLocationsResponse';
import useDebounce from '../../../hooks/useDebounce';

const defaultScoreRange = [1, 5];

const LocationHistory: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { user, login } = useContext(GMBAuthContext);
  const [loading, setLoading] = useState(false);
  const [locationResponse, setLocationResponse] = useState<IGMBLocationsResponse>({
    locations: [],
    paging: {
      totalItems: 0,
      pagesTotal: 0,
    },
    title: [],
  });
  const [locationOptions, setLocationOptions] = useState<IGmbLocation[]>([]);
  const [locationSearch, setLocationSearch] = useState('');
  const debouncedLocationSearch = useDebounce(locationSearch, 500);
  const [orderBy, setOrderBy] = useState<string>('title asc');
  const [offset, setOffset] = useState<number>(0);
  const [pageSize, setPageSize] = useState<string>('10');
  const [page, setPage] = useState<number>(1);
  const [draftLocation, setDraftLocation] = useState<IGmbLocation | null>(null);
  const [appliedLocation, setAppliedLocation] = useState<IGmbLocation | null>(null);
  const [draftScore, setDraftScore] = useState<number[]>(defaultScoreRange);
  const [appliedScore, setAppliedScore] = useState<number[]>(defaultScoreRange);

  useEffect(() => {
    setCurrentView(t('location') + t('history').toLowerCase());
    document.title = `repraise ${t('location') + t('history').toLowerCase()}`;
  }, [setCurrentView, t]);

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();
    // The page owns its single loading state so aborted and failed requests always clear it in finally.
    const loadLocations = async () => {
      setLoading(true);

      try {
        const filters = {
          locationIDs: appliedLocation ? [appliedLocation.locationID] : undefined,
          ...(appliedScore[0] !== defaultScoreRange[0] || appliedScore[1] !== defaultScoreRange[1]
            ? { scoreMin: appliedScore[0], scoreMax: appliedScore[1] }
            : {}),
        };
        const data = await GMBService.getAllLocations(
          offset,
          parseInt(pageSize),
          () => {},
          controller.signal,
          undefined,
          orderBy === '' ? undefined : [orderBy],
          undefined,
          filters
        );

        if (data && !('success' in data)) {
          setLocationResponse(data);
        }
      } finally {
        setLoading(false);
      }
    };

    void loadLocations();

    return () => {
      controller.abort();
    };
  }, [appliedLocation, appliedScore, offset, orderBy, pageSize, user]);

  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();

    const loadLocationOptions = async () => {
      const data = await GMBService.getAllLocations(
        0,
        25,
        () => {},
        controller.signal,
        undefined,
        undefined,
        debouncedLocationSearch
      );

      if (data && !('success' in data)) {
        setLocationOptions(data.locations);
      }
    };

    void loadLocationOptions();

    return () => {
      controller.abort();
    };
  }, [debouncedLocationSearch, user]);

  const handlePageNumberChange = (event: ChangeEvent<unknown>, pageNumber: number) => {
    setPage(pageNumber);
    setOffset(parseInt(pageSize) * pageNumber - parseInt(pageSize));
  };

  const handlePageSizeChange = (event: SelectChangeEvent) => {
    setOffset(0);
    setPage(1);
    setPageSize(event.target.value);
  };

  const handleApplyFilters = () => {
    setAppliedLocation(draftLocation);
    setAppliedScore(draftScore);
    setOffset(0);
    setPage(1);
  };

  const handleResetFilters = () => {
    setDraftLocation(null);
    setAppliedLocation(null);
    setDraftScore([...defaultScoreRange]);
    setAppliedScore([...defaultScoreRange]);
    setOffset(0);
    setPageSize('10');
    setPage(1);
  };

  const renderLocationFilterOption = (props: any, option: IGmbLocation) => {
    const { key, ...optionProps } = props;
    return (
      <Box
        key={key}
        component="li"
        {...optionProps}
      >
        <Box>
          <Typography>{option.locationData.title}</Typography>
          <Typography sx={{ fontSize: '10px' }}>
            {`(${option.locationData.address.addressLines}, ${option.locationData.address.postalCode} ${option.locationData.address.locality})`}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Section>
      {!user ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <GoogleMyBusinessLogin login={login} />
        </Box>
      ) : (
        <ContentContainer>
          <LeftContainer>
            <ActionBar />
            <SortContainer>
              <Typography variant="body2">
                {`${locationResponse.paging.totalItems} ${t('locations')}`}
              </Typography>
              <FormControl
                size="small"
                sx={{ minWidth: '17.75rem' }}
              >
                <InputLabel id="sort-select-label">{t('sort')}</InputLabel>
                <Select
                  labelId="sort-select-label"
                  value={orderBy}
                  onChange={(event: SelectChangeEvent) => {
                    setOffset(0);
                    setPage(1);
                    setOrderBy(event.target.value);
                  }}
                  label={t('sort')}
                >
                  <MenuItem value="">
                    <em>{t('unsorted')}</em>
                  </MenuItem>
                  <MenuItem value="title asc">{t('location_name_asc')}</MenuItem>
                  <MenuItem value="title desc">{t('location_name_desc')}</MenuItem>
                  <MenuItem value="score asc">{t('score_asc')}</MenuItem>
                  <MenuItem value="score desc">{t('score_desc')}</MenuItem>
                  <MenuItem value="reviewCount asc">{t('review_count_asc')}</MenuItem>
                  <MenuItem value="reviewCount desc">{t('review_count_desc')}</MenuItem>
                  <MenuItem value="growthPercentage asc">{t('growth_percentage_asc')}</MenuItem>
                  <MenuItem value="growthPercentage desc">{t('growth_percentage_desc')}</MenuItem>
                </Select>
              </FormControl>
            </SortContainer>
            {loading ? (
              <Box>
                <LinearProgress sx={{ my: theme.spacing(1) }} />
              </Box>
            ) : locationResponse.locations.length === 0 ? (
              <Box>
                <Typography variant="body2">{t('no_records_found')}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {locationResponse.locations.map((location) => (
                  <Box key={location.locationID}>
                    <LocationHistoryCard
                      locationName={location.locationData.title}
                      score={location.score.averageRating}
                      growth={location.score.growthPercentage}
                      reviews={location.score.totalReviewCount}
                      locationID={location.locationID}
                      addressLines={location.locationData.address.addressLines}
                      phoneNumber={location.locationData.phoneNumber}
                      zip={location.locationData.address.postalCode}
                      city={location.locationData.address.locality}
                      latitude={location.locationData.location.latitude}
                      longitude={location.locationData.location.longitude}
                      times={location.locationData.times}
                      status={location.locationData.status}
                    />
                  </Box>
                ))}
              </Box>
            )}
            <Pagination
              count={locationResponse.paging.pagesTotal}
              page={page}
              itemDescription={t('locations')}
              showItemsPerPage={pageSize}
              onPageChange={handlePageNumberChange}
              onItemsPerPageChange={handlePageSizeChange}
            />
          </LeftContainer>
          <RightContainer>
            <Filter
              disabled={loading}
              filters={
                <>
                  <FormControl
                    disabled={loading}
                    size="small"
                    sx={{ minWidth: '284px', mt: theme.spacing(2) }}
                  >
                    <Autocomplete
                      value={draftLocation}
                      getOptionLabel={(location) => location.locationData.title}
                      getOptionKey={(location) => location.locationID}
                      options={locationOptions}
                      isOptionEqualToValue={(option, value) =>
                        option.locationID === value.locationID
                      }
                      onInputChange={(event, value) => setLocationSearch(value)}
                      onChange={(event, value) => setDraftLocation(value)}
                      renderOption={renderLocationFilterOption}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={t('location')}
                        />
                      )}
                    />
                  </FormControl>
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'uppercase',
                      fontWeight: '700',
                      mt: theme.spacing(2),
                      color: loading ? theme.palette.text.disabled : theme.palette.text.primary,
                    }}
                  >
                    {t('score')}
                  </Typography>
                  <SliderStarsAsLabels
                    disabled={loading}
                    value={draftScore}
                    onChange={(event, value) => setDraftScore(value as number[])}
                  />
                  <Divider sx={{ mt: theme.spacing(1) }} />
                  <Button
                    disabled={loading}
                    variant="contained"
                    onClick={handleApplyFilters}
                    sx={{ mt: theme.spacing(1) }}
                  >
                    {t('apply')}
                  </Button>
                </>
              }
              activeFilters={
                <>
                  {appliedLocation && (
                    <Chip
                      label={appliedLocation.locationData.title}
                      onDelete={() => {
                        setDraftLocation(null);
                        setAppliedLocation(null);
                        setOffset(0);
                        setPage(1);
                      }}
                    />
                  )}
                  {(appliedScore[0] !== 1 || appliedScore[1] !== 5) && (
                    <Chip
                      label={`${t('stars')} ${appliedScore[0]} - ${appliedScore[1]}`}
                      onDelete={() => {
                        setDraftScore([...defaultScoreRange]);
                        setAppliedScore([...defaultScoreRange]);
                        setOffset(0);
                        setPage(1);
                      }}
                    />
                  )}
                </>
              }
              resetActiveFilters={handleResetFilters}
            />
          </RightContainer>
        </ContentContainer>
      )}
    </Section>
  );
};

export default LocationHistory;
